import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Card, 
  CardContent, 
  Divider, 
  LinearProgress,
  Grid,
  Paper
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import StarIcon from '@mui/icons-material/Star';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { makeRequest } from '../../../assets/functions_helper';

export default function SurveyResultsDialog({ surveyId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!surveyId) return;
    
    setLoading(true);
    setError(null);
    makeRequest(
      "GET",
      `surveys/results/${surveyId}`,
      null,
      "application/json",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType")
    )
      .then((res) => {
        setData(res);
      })
      .catch((err) => {
        console.error("Error fetching survey results:", err);
        setError("No se pudieron cargar los resultados de esta encuesta.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [surveyId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>Generando estadísticas...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!data) return null;

  // Estado vacío: 0 sets de respuesta
  if (data.total_response_sets === 0) {
    return (
      <Box sx={{ p: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <AssessmentIcon sx={{ fontSize: 80, color: '#e0e0e0', mb: 2 }} />
        <Typography variant="h6" sx={{ color: '#424242', fontWeight: 600 }}>
          Aún no hay respuestas recopiladas
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, maxWidth: 350 }}>
          Los clientes no han finalizado esta encuesta todavía. Vuelve más tarde cuando haya interacción.
        </Typography>
      </Box>
    );
  }

  // Renderizado dinámico de la data
  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Header Metric */}
      <Card sx={{ mb: 4, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', boxShadow: 'none', borderRadius: 2 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>
              Participación Total
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 900, color: '#0f172a', mt: 0.5 }}>
              {data.total_response_sets}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              encuestas completadas por clientes
            </Typography>
          </Box>
          <AnalyticsIcon sx={{ fontSize: 60, color: '#cbd5e1' }} />
        </CardContent>
      </Card>

      {/* Preguntas Iteration */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {data.questions && data.questions.map((q, index) => {

          // Renderizar Barras de Números/Estrellas
          if (q.type === 'numeric_rating') {
            const hasResponses = q.total_responses > 0;
            return (
              <Box key={q.question_id}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
                  {index + 1}. {q.text}
                </Typography>
                
                <Grid container spacing={4} alignItems="center">
                  <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h2" sx={{ fontWeight: 900, color: '#eab308', mr: 1 }}>
                        {hasResponses ? q.average.toFixed(1) : "0.0"}
                      </Typography>
                      <StarIcon sx={{ fontSize: 40, color: '#eab308' }} />
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      Promedio de {q.total_responses} votos
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    {[5, 4, 3, 2, 1].map((starValue) => {
                      const count = q.distribution ? (q.distribution[starValue] || 0) : 0;
                      // Evitar division por 0
                      const percentage = hasResponses ? (count / q.total_responses) * 100 : 0;
                      
                      return (
                        <Box key={starValue} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 20, color: '#64748b' }}>
                            {starValue}
                          </Typography>
                          <StarIcon sx={{ fontSize: 16, color: '#94a3b8', mr: 2 }} />
                          <Box sx={{ flexGrow: 1, mr: 2 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={percentage} 
                              sx={{ 
                                height: 10, 
                                borderRadius: 5, 
                                backgroundColor: '#f1f5f9',
                                '& .MuiLinearProgress-bar': { backgroundColor: '#eab308', borderRadius: 5 }
                              }} 
                            />
                          </Box>
                          <Typography variant="body2" sx={{ minWidth: 30, textAlign: 'right', fontWeight: 500, color: '#475569' }}>
                            {count}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Grid>
                </Grid>
                {index < data.questions.length - 1 && <Divider sx={{ mt: 4 }} />}
              </Box>
            );
          }

          // Renderizar Lista de Texto
          if (q.type === 'open_ended') {
            return (
              <Box key={q.question_id}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    {index + 1}. {q.text}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    {q.total_responses} respuestas
                  </Typography>
                </Box>
                
                {q.total_responses === 0 ? (
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    No hay comentarios registrados para esta pregunta.
                  </Typography>
                ) : (
                  <Box sx={{ 
                    maxHeight: 300, 
                    overflowY: 'auto', 
                    pr: 1, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 2,
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: '4px' }
                  }}>
                    {q.responses && q.responses.map((resp) => {
                      const dateObj = new Date(resp.submitted_at);
                      const formattedDate = dateObj.toLocaleDateString('es-ES', { 
                        month: 'short', day: 'numeric', hour: '2-digit', minute:'2-digit' 
                      });

                      return (
                        <Paper key={resp.response_id} variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', borderColor: '#e2e8f0' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: '#94a3b8', mr: 1 }} />
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                              {formattedDate}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.5 }}>
                            "{resp.text}"
                          </Typography>
                        </Paper>
                      );
                    })}
                  </Box>
                )}
                {index < data.questions.length - 1 && <Divider sx={{ mt: 4 }} />}
              </Box>
            );
          }

          return null; // fallback para tipos no conocidos
        })}
      </Box>
    </Box>
  );
}
